import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useLocation, useHistory } from 'react-router-dom'
import { Button, useNotificationService } from '@abroad/components'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { AccountsTab, MyCalendarsTab } from './index'

export const CALENDAR_TAB_TYPES = {
  ACCOUNTS: 'accounts',
  MY_CALENDARS: 'myCalendars',
}

const CalendarTabs = ({ activeTab, onTabChange, isCalendarConnected }) => {
  const myCalendarsButton = (
    <Button
      variant='none'
      className='navTabs'
      disabled={!isCalendarConnected}
      style={{
        background:
          activeTab === CALENDAR_TAB_TYPES.MY_CALENDARS ? '#0A223C' : '',
        color: activeTab === CALENDAR_TAB_TYPES.MY_CALENDARS ? '#fff' : '',
        opacity: !isCalendarConnected ? 0.5 : 1,
        cursor: !isCalendarConnected ? 'not-allowed' : 'pointer',
      }}
      onClick={() => {
        if (isCalendarConnected) {
          onTabChange(CALENDAR_TAB_TYPES.MY_CALENDARS)
        }
      }}>
      MY CALENDARS
    </Button>
  )

  return (
    <div className='mb-4'>
      <Button
        variant='none'
        className='navTabs ml-0'
        style={{
          background:
            activeTab === CALENDAR_TAB_TYPES.ACCOUNTS ? '#0A223C' : '',
          color: activeTab === CALENDAR_TAB_TYPES.ACCOUNTS ? '#fff' : '',
        }}
        onClick={() => onTabChange(CALENDAR_TAB_TYPES.ACCOUNTS)}>
        ACCOUNTS
      </Button>
      {!isCalendarConnected ? (
        <OverlayTrigger
          placement='top'
          overlay={
            <Tooltip id='my-calendars-disabled-tooltip'>
              You need to connect your abroad calendar first
            </Tooltip>
          }>
          <span className='d-inline-block'>{myCalendarsButton}</span>
        </OverlayTrigger>
      ) : (
        myCalendarsButton
      )}
    </div>
  )
}

const CalendarTab = ({
  connectedCalendars = [],
  onRefreshCalendars,
  isCalendarConnected = false,
}) => {
  const [activeTab, setActiveTab] = useState(CALENDAR_TAB_TYPES.ACCOUNTS)
  const location = useLocation()
  const history = useHistory()
  const Notification = useNotificationService()

  const handleTabChange = (tab) => {
    // Prevent switching to MY_CALENDARS if Abroad calendar is not connected
    if (tab === CALENDAR_TAB_TYPES.MY_CALENDARS && !isCalendarConnected) {
      return
    }
    setActiveTab(tab)
  }

  // Redirect to ACCOUNTS tab if user tries to access MY_CALENDARS when disabled
  useEffect(() => {
    if (!isCalendarConnected && activeTab === CALENDAR_TAB_TYPES.MY_CALENDARS) {
      setActiveTab(CALENDAR_TAB_TYPES.ACCOUNTS)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalendarConnected])

  // Check for error query parameters and show notifications, or redirect to my_calendar tab
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const isConnected = searchParams.get('isConnected')
    const error = searchParams.get('error')
    const tab = searchParams.get('tab')

    // Handle error cases
    if (isConnected === 'false' && error) {
      let errorMessage = ''

      if (error === 'AUTH_FAILED') {
        errorMessage =
          'Authentication cancelled. Please connect your account to continue.'
      } else if (error === 'CALENDAR_PERMISSIONS_DENIED') {
        errorMessage =
          'Calendar access is required. Please allow all permissions and try again.'
      }

      if (errorMessage) {
        Notification.showNotification(errorMessage, 'danger')

        // Clean up URL by removing query parameters to avoid showing error again on refresh
        const newSearchParams = new URLSearchParams(location.search)
        newSearchParams.delete('isConnected')
        newSearchParams.delete('error')
        newSearchParams.delete('provider')
        const newSearch = newSearchParams.toString()
        const newPath = newSearch
          ? `${location.pathname}?${newSearch}`
          : location.pathname
        history.replace(newPath)
      }
    }
    // Handle successful connection - redirect to my_calendar tab
    // Only redirect if Abroad calendar is connected
    else if (isConnected === 'true' && tab === 'my_calendar') {
      if (isCalendarConnected) {
        setActiveTab(CALENDAR_TAB_TYPES.MY_CALENDARS)
      }

      // Clean up URL by removing query parameters
      const newSearchParams = new URLSearchParams(location.search)
      newSearchParams.delete('isConnected')
      newSearchParams.delete('tab')
      newSearchParams.delete('provider')
      newSearchParams.delete('email')
      const newSearch = newSearchParams.toString()
      const newPath = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname
      history.replace(newPath)
    } else if (isConnected === 'true') {
      const newSearchParams = new URLSearchParams(location.search)
      newSearchParams.delete('isConnected')
      newSearchParams.delete('provider')
      const newSearch = newSearchParams.toString()
      const newPath = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname
      history.replace(newPath)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isCalendarConnected])

  return (
    <div>
      <CalendarTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isCalendarConnected={isCalendarConnected}
      />
      {activeTab === CALENDAR_TAB_TYPES.ACCOUNTS && (
        <AccountsTab
          connectedCalendars={connectedCalendars}
          onRefreshCalendars={onRefreshCalendars}
        />
      )}
      {activeTab === CALENDAR_TAB_TYPES.MY_CALENDARS && (
        <>
          <p className='s6 text-muted mb-0'>
            Switch on every calendar whose events should block client bookings.
          </p>
          <MyCalendarsTab />
        </>
      )}
    </div>
  )
}

CalendarTab.propTypes = {
  connectedCalendars: PropTypes.array,
  onRefreshCalendars: PropTypes.func,
  isCalendarConnected: PropTypes.bool,
}

export default CalendarTab
